import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getStripePlans,
  getProductsWithPrices,
  getActivePriceConfig,
  setActivePriceConfig,
  PlanMetrics,
  ProductWithPrices,
  ActivePriceConfig,
} from '@/api/stripeService';
import { queryKeys } from '@/lib/queryKeys';
import { toaster } from '@/components/ui/toaster';

export const useAdminStripe = () => {
  const queryClient = useQueryClient();

  const { data: plansData, isLoading: plansLoading } = useQuery<{ plans: PlanMetrics[] }>({
    queryKey: queryKeys.stripePlans,
    queryFn: getStripePlans,
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: products = [], isLoading: productsLoading } = useQuery<ProductWithPrices[]>({
    queryKey: queryKeys.stripeProducts,
    queryFn: getProductsWithPrices,
  });

  const { data: activePriceConfig } = useQuery<ActivePriceConfig>({
    queryKey: queryKeys.stripeActivePrice,
    queryFn: getActivePriceConfig,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.stripePlans });
    queryClient.invalidateQueries({ queryKey: queryKeys.stripeProducts });
    queryClient.invalidateQueries({ queryKey: queryKeys.stripeActivePrice });
  };

  const setActivePriceMutation = useMutation({
    mutationFn: ({
      monthlyPriceId,
      yearlyPriceId,
      quarterlyPriceId,
    }: {
      monthlyPriceId: string;
      yearlyPriceId: string;
      quarterlyPriceId: string | null;
    }) => setActivePriceConfig(monthlyPriceId, yearlyPriceId, quarterlyPriceId),
    onSuccess: () => {
      invalidateAll();
      toaster.create({
        title: 'Prix actif mis à jour',
        description: 'La configuration de prix sélectionnée est désormais active',
        type: 'success',
      });
    },
    onError: (error) => {
      toaster.create({
        title: 'Échec de la mise à jour du prix actif',
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        type: 'error',
      });
    },
  });

  return {
    plans: plansData?.plans ?? [],
    plansLoading,
    products,
    productsLoading,
    activePriceConfig,
    setActivePrice: setActivePriceMutation.mutateAsync,
    isSettingActive: setActivePriceMutation.isPending,
  };
};
